import React from 'react';
import RangeSlider from './RangeSlider';
import TextField from '../audi-ui-components/TextField';
import Select from '../audi-ui-components/Select';
import Modal from '../audi-ui-components/Modal';
import Button from '../audi-ui-components/Button';
import AFSDisclaimer from './legal/AFSDisclaimer';
import { formatDollar, unFormatDollar, gtmPush } from '../lib/helpers';
import LoadingSpinner from './LoadingSpinner';
import { request } from '../lib/apiRequestWrapper';

class FinanceCalculator extends React.Component {


  constructor(props) {
    super(props);
    this.state = {
      loading: false,

      rate: 0,
      deposit: 0,
      fees: 0,
      term: 0,
      termMax: 0,
      termStep: 0,
      termOptions: [],
      km: 0,
      kmOptions: [],
      balloon: 0,
      balloonOptions: [],
      balloonMax: 0,
      balloonMin: 0,
      repayWeekly: 0,
      repayMonthly: 0,
      balloonAmount: 0,
      GFV: 0,

      financePurpose: "business",
      financeProduct: "AudiChoice",
      depositHasFocus: false,
      showBreakdown: false,
      showDetail: false,
      showDisclaimer: false,
      summary: 'total', // 'monthly'
      gtmInteractCart: false,
      gtmInteractFinance: false,

      disclaimerOpen: false,
      description: '',
      disclaimer: '',
    }
    this.refPriceBox = React.createRef();
    // set to true to enable tracing in console
    this.enableTracing = process.env.RAZZLE_APP_ENV !== "production";
  }

  componentDidMount() {
    this.getDefaults();
  }

  //when the loan product selected changes, get the default values
  getDefaults = () => {
    if (this.enableTracing) {
      console.log('*********** getDefaults()');
    }
    const { amount, modelYear, modelCode } = this.props;
    let payload = {
      "product": {
        "id": this.state.financeProduct,
      },
      "vehicle": {
        "key": modelCode + '/' + modelYear.toString().slice(-2),
        "priceTotal": amount,
        "priceModel": amount
      },
      "domain": "AUDI.CC.AUS",
      "name": "Defaults"
    };

    if (this.enableTracing) {
      console.log(payload);
    }

    this.setLoading(true);
    request(
      `${process.env.RAZZLE_MYAUDI_API}/1/afs/default`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    ).then((response) => {
      this.setDefaults(response);
      this.setLoading(false);
    }).catch((error) => {
      if (this.enableTracing) {
        console.error(error);
      }
      this.setLoading(false);
    });
  }

  //set the default values for the selected loan product based on api response
  setDefaults = (data) => {

    if (this.enableTracing) {
      console.log('------- setDefaults()');
      console.log(data);
    }

    //grab the collection of default values
    const parameters = data?.parameters?.group?.parameter;

    //rate
    const tempRate = parameters?.find(item => item.id === 'ComparisonRate')?.dataValues ?? parameters?.find(item => item.id === 'AnnualRate')?.dataValues;
    const rateDefault = (tempRate / 100).toFixed(4);
    if (this.enableTracing) {
      console.log('Rate Default:', rateDefault);
    }

    //term
    const termDefault = parameters?.find(item => item.id === 'Term')?.default;
    const termOptions = parameters?.find(item => item.id === 'Term')?.dataValues ?? [];
    const termMax = termOptions.length > 0 ? Number(termOptions[termOptions.length - 1]) : 0;
    const termStep = termOptions.length > 0 ? Number(termOptions[1] - termOptions[0]) : 12; //this is not going to work unless the passed options are evenly spaced in value
    if (this.enableTracing) {
      console.log('Term Default:', termDefault);
      console.log('Term Options:', termOptions);
      console.log('Term Max:', termMax);
      console.log('Term Step:', termStep);
    }

    //deposit
    const depositDefault = parameters?.find(item => item.id === 'DownPayment')?.dataValues;
    if (this.enableTracing) {
      console.log('Deposit Default:', depositDefault);
    }

    //balloon
    const balloonDefault = parameters?.find(item => item.id === 'Balloon')?.default;
    const balloonOptions = parameters?.find(item => item.id === 'Balloon')?.dataValues ?? [];
    const balloonMax = balloonOptions.length > 0 ? Number(((Number(balloonOptions[balloonOptions.length - 1])) / 100).toFixed(4)) : 0;
    const balloonMin = balloonOptions.length > 0 ? Number(((Number(balloonOptions[0])) / 100).toFixed(4)) : 0;
    if (this.enableTracing) {
      console.log('Balloon Default:', balloonDefault);
      console.log('Balloon Options:', balloonOptions);
      console.log('Balloon Max:', balloonMax);
      console.log('Balloon Min:', balloonMin);
    }

    //KM limit
    const kmLimitDefault = parameters?.find(item => item.id === 'KMLimit')?.default;
    const kmLimitOptions = parameters?.find(item => item.id === 'KMLimit')?.dataValues ?? [];
    if (this.enableTracing) {
      console.log('KM Limit Default:', kmLimitDefault);
      console.log('KM Limit Options:', kmLimitOptions);
    }

    //disclaimer text
    const description = data?.parameters?.description ?? '';
    const disclaimer = data?.parameters?.productDisclaimer ?? '';

    //set state
    this.setState({
      rate: parseFloat(rateDefault),
      termMax: termMax,
      termStep: termStep,
      term: termDefault,
      termOptions: termOptions ?? [],
      deposit: depositDefault,
      km: kmLimitDefault,
      kmOptions: kmLimitOptions ?? [],
      balloon: balloonDefault,
      balloonOptions: balloonOptions ?? [],
      balloonMax: balloonMax,
      balloonMin: balloonMin,
      description: description,
      disclaimer: disclaimer
    });

    this.setCalculation(data);
  }

  setLoading = (isLoading) => {
    this.setState({ loading: isLoading });
  }

  calculate = () => {
    if (this.enableTracing) {
      console.log('*********** calculate()');
    }

    let payloadParams = [
      {
        "id": "FinanceProduct",
        "changed": true
      },
      {
        "id": "DownPayment",
        "text": this.state.deposit,
        "changed": true
      },
      {
        "id": "KMLimit",
        "text": this.state.km,
        "changed": true
      },
      {
        "id": "MaskRate",
        "text": this.state.rate * 100,
        "changed": true
      }
    ];
    if (this.state.term && this.state.term !== null) {
      payloadParams.push({
        "id": "Term",
        "text": this.state.term,
        "changed": true
      });
    }
    if (this.state.balloon && this.state.balloon !== null) {
      payloadParams.push({
        "id": "Balloon",
        "text": this.state.balloon * 100,
        "changed": true
      });
    }

    const { amount, modelYear, modelCode } = this.props;
    let payload = {
      "product": {
        "parameter": payloadParams,
        "id": this.state.financeProduct,
        //"text": data.loantype
      },
      "vehicle": {
        "key": modelCode,
        "year": modelYear,
        "priceTotal": amount,
        "priceModel": amount
      },
      "domain": "AUDI.CC.AUS",
      "name": "Defaults"
    };

    this.setLoading(true);
    request(
      `${process.env.RAZZLE_MYAUDI_API}/1/afs/calculator`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    ).then((response) => {
      if (this.enableTracing) {
        console.log(response);
      }
      this.setCalculation(response);
      this.setLoading(false);
    }).catch((error) => {
      if (this.enableTracing) {
        console.error(error);
      }
      this.setLoading(false);
    });
  }
  
  setCalculation = (data) => {

    //grab the collection of default values
    const parameters = data?.parameters?.group?.parameter;

    //repayment
    const repayWeekly = parameters?.find(item => item.id === 'Rate')?.dataValues ?? 0;
    const repayMonthly = (repayWeekly * 52 / 12).toFixed(2);
    if (this.enableTracing) {
      console.log('Repay Weekly:', repayWeekly);
      console.log('Repay Monthly:', repayMonthly);
    }

    //balloon amount
    const balloonAmount = parameters?.find(item => item.id === 'BalloonAmount')?.dataValues;
    if (this.enableTracing) {
      console.log('Balloon Amount:', balloonAmount);
    }

    //GFV
    const GFV = parameters?.find(item => item.id === 'GFV')?.dataValues;
    if (this.enableTracing) {
      console.log('GFV:', GFV);
    }

    //set state
    this.setState({
      repayWeekly: repayWeekly,
      repayMonthly: repayMonthly,
      balloonAmount: balloonAmount,
      GFV: GFV
    });
  }

  openDisclaimer = () => {
    this.setState({ disclaimerOpen: true });
    this.onInteractFinance();
  }
  closeDisclaimer = () => {
    this.setState({ disclaimerOpen: false });
  }

  onInteractFinance = () => {
    if (!this.state.gtmInteractFinance) {
      let gtmEventCat = this.props.isUsed ? "VTP Used" : "VTP"
      gtmPush(gtmEventCat, 'Clicked FinanceTool', this.props.model);
      this.setState({ gtmInteractFinance: true });
    }
  }

  render() {
    const { amount, isUsed, theme } = this.props;
    const audiTheme = theme === 'dark' ? 'light' : 'dark';
    const financeProductOpts = isUsed ? {
      "personal": [{ value: 'ConsumerLoan', label: 'Consumer Loan' }],
      "business": [{ value: 'ChattelMortgage', label: 'Chattel Mortgage' }]
    } : {
      "personal": [{ value: 'AudiChoice', label: 'Audi Choice' }, { value: 'ConsumerLoan', label: 'Consumer Loan' }],
      "business": [{ value: 'AudiChoice', label: 'Audi Choice' }, { value: 'ChattelMortgage', label: 'Chattel Mortgage' }]
    };

    return (
      <div className="finance-calculator position-relative" ref={this.refPriceBox}>
        {this.state.loading && <LoadingSpinner />}
        <p className="aui-headline-4 mb-2"><b>Audi Financial Services</b><sup>VI</sup></p>
        <p className="des-font mb-2">
          <span className="aui-headline-4">{formatDollar(this.state.repayMonthly, true)} / mo<sup>VII</sup></span><br />
          <span className="small-text">Estimated loan repayment</span>
        </p>
        {this.state.balloonAmount &&
          <p className="des-font mb-2">
            <span className="aui-headline-5">{formatDollar(this.state.balloonAmount, true)}<sup>VIII</sup></span><br />
            <span className="small-text">Final balloon repayment</span>
          </p>
        }
        {this.state.GFV &&
          <p className="des-font mb-2">
            <span className="aui-headline-5">{formatDollar(this.state.GFV, true)}</span><br />
            <span className="small-text">Guaranteed Future Value</span>
          </p>
        }
        <RangeSlider
          min={0.01}
          max={0.2}
          step={0.0001}
          value={this.state.rate}
          label={(<>Interest Rate<sup>IX</sup></>)}
          labels={{ 0.01: "1%", 0.2: "20%" }}
          onChange={(v) => {
            this.setState({
              rate: parseFloat(v.toFixed(4))
            });
          }}
          onChangeComplete={() => { this.onInteractFinance(); this.calculate(); }}
          format={(v) => { return `${(v * 100).toFixed(2)}%`; }}
          className="mb-3"
        />
        <Select
          name="purpose"
          label="Purpose"
          value={this.state.financePurpose}
          options={[{ value: 'personal', label: 'Personal' }, { value: 'business', label: 'Business' }]}
          onChange={(v) => {
            this.onInteractFinance();
            this.setState({
              financePurpose: v,
              financeProduct: financeProductOpts[v][0].value,
            }, () => { this.getDefaults(); });

          }}
          className="mb-4"
          theme={audiTheme}
        />
        <Select
          name="product"
          label="Finance Product"
          value={this.state.financeProduct}
          options={financeProductOpts[this.state.financePurpose]}
          onChange={(v) => {
            this.onInteractFinance();
            this.setState({
              financeProduct: v
            }, () => { this.getDefaults(); });
          }}
          className="mb-4"
          theme={audiTheme}
        />
        <RangeSlider
          min={0}
          max={this.state.termMax}
          step={this.state.termStep}
          value={Number(this.state.term)}
          label="Term"
          labels={this.state.termOptions.reduce((obj, v) => { obj[v] = v; return obj; }, {})}
          onChange={(v) => {
            this.setState({
              term: v,
            });
          }}
          onChangeComplete={() => { this.onInteractFinance(); this.calculate(); }}
          format={(v) => { return `${v} mths`; }}
          className="mb-3"
        />

        <TextField
          name="deposit"
          label="Deposit"
          className="mb-4"
          theme={audiTheme}
          value={this.state.depositHasFocus ? this.state.deposit : formatDollar(this.state.deposit)}
          onFocus={() => { this.setState({ depositHasFocus: true }); }}
          onBlur={() => { this.setState({ depositHasFocus: false }); }}
          onChange={(v) => {
            this.onInteractFinance();
            this.setState({
              deposit: unFormatDollar(v),
            }, () => { this.calculate(); });
          }}
        />
        {this.state.balloon >= 0 &&
          <RangeSlider
            min={this.state.balloonMin}
            max={this.state.balloonMax}
            step={0.01}
            disabled={false}
            value={Number(this.state.balloon)}
            label="Balloon"
            labels={{ 0: "0%", 0.5: ((this.state.balloonMax * 100) + '%') }}
            onChange={(v) => { this.setState({ balloon: v }); }}
            onChangeComplete={
              () => {
                this.onInteractFinance();
                this.calculate();
              }
            }
            format={(v) => { return `${(v * 100).toFixed(0)}%`; }}
            className="mb-5"
          />
        }
        {this.state.km &&
          <Select
            name="kmLimit"
            label="Annual KM Limit"
            value={this.state.km}
            options={this.state.kmOptions?.map(km => ({ value: km, label: km }))}
            onChange={(v) => {
              this.onInteractFinance();
              this.setState({
                km: v,
              }, () => { this.calculate(); });
            }}
            className="mb-4"
            theme={audiTheme}
          />
        }
        <Button onClick={this.openDisclaimer} className="d-none d-large-block" variant="text" theme={audiTheme}>Disclaimer</Button>
        <Modal isActive={this.state.disclaimerOpen} variant="layer" closeButton close={this.closeDisclaimer} >
          <AFSDisclaimer description={this.state.description} disclaimer={this.state.disclaimer} />
        </Modal>
      </div>
    );
  }
}

export default FinanceCalculator;
